/**
 * SKE-4701 — one stack manager for the app's top banners.
 *
 * Every banner is independently `sticky top-0 z-50` and they are mounted as
 * siblings in `root.tsx`, so two that want to render at the same time overlap.
 * Only one pair was ever protected, and in state rather than layout:
 * `use-subscription-banner-state.ts` hides the subscription banner while
 * invoices are due. Nothing stopped an incident banner and a billing banner
 * landing on top of each other.
 *
 * This helper answers ONE question, "may I render", and does nothing else.
 *
 * TWO PROPERTIES IT MUST KEEP.
 *
 * Dismissal storage stays with each banner. This never reads or writes a
 * storage key, so a banner's snooze behaviour is exactly what it was.
 *
 * SUPPRESSED IS NOT DISMISSED. A banner hidden behind a higher-ranked one has
 * not been seen, so it has not been snoozed: when the outage clears, the
 * campaign banner returns with its dismissal budget untouched. That falls out
 * of the design rather than being special-cased, because suppression happens
 * at render and dismissal only ever happens on a click.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useSyncExternalStore,
} from "react";

/**
 * Highest priority first.
 *
 * An incident outranks money, money outranks the campaign. Due invoices sit
 * above the subscription banner, preserving the relationship those two already
 * had in `use-subscription-banner-state.ts`, which is left in place: this
 * helper adds a layout rule and removes no existing state rule.
 */
export const BANNER_PRECEDENCE = [
  "service-status",
  "due-invoices",
  "subscription",
  "instagram-campaign",
] as const;

export type BannerId = (typeof BANNER_PRECEDENCE)[number];

class BannerStack {
  private claims = new Map<BannerId, boolean>();
  private listeners = new Set<() => void>();

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  claim(id: BannerId, wantsToRender: boolean): void {
    if ((this.claims.get(id) ?? false) === wantsToRender) return;
    this.claims.set(id, wantsToRender);
    for (const listener of this.listeners) listener();
  }

  /** True when something ranked above `id` currently wants the slot. */
  isBlocked = (id: BannerId): boolean => {
    const rank = BANNER_PRECEDENCE.indexOf(id);
    if (rank <= 0) return false;
    return BANNER_PRECEDENCE.slice(0, rank).some((higher) =>
      this.claims.get(higher)
    );
  };
}

const BannerStackContext = createContext<BannerStack | null>(null);

export function BannerStackProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  /*
   * A ref, not `useMemo`. `useMemo` is a performance HINT: React is free to
   * discard a memo and recompute it. A fresh `BannerStack` would reset every
   * claim at once, unblocking all four banners into the same sticky offset,
   * which is the exact failure this provider exists to prevent. Identity has
   * to be guaranteed, so it is stored rather than memoised.
   */
  const stackRef = useRef<BannerStack | null>(null);
  if (!stackRef.current) stackRef.current = new BannerStack();
  const stack = stackRef.current;
  return (
    <BannerStackContext.Provider value={stack}>
      {children}
    </BannerStackContext.Provider>
  );
}

/**
 * Claims this banner's place in the stack and answers whether it may render.
 *
 * Call it unconditionally, before any early return, like every other hook.
 *
 * With no provider above it this returns `wantsToRender` unchanged. That is
 * deliberate: a banner rendered on its own, in a test or a story, should behave
 * as it always did rather than silently disappear because a provider was
 * forgotten.
 */
export function useBannerSlot(id: BannerId, wantsToRender: boolean): boolean {
  const stack = useContext(BannerStackContext);

  /*
   * useLAYOUTEffect, not useEffect. A plain effect runs AFTER paint, so on the
   * first commit every banner that wants the slot reads an empty claims map,
   * renders, and the losers are removed a frame later. That is a visible stack
   * of overlapping sticky banners followed by a jump. Claiming before paint
   * means the browser only ever draws the winner.
   */
  useLayoutEffect(() => {
    if (!stack) return;
    stack.claim(id, wantsToRender);
    // Releasing on unmount matters: a banner that navigated away must not keep
    // suppressing the one below it.
    return () => stack.claim(id, false);
  }, [stack, id, wantsToRender]);

  const blocked = useSyncExternalStore(
    stack ? stack.subscribe : noopSubscribe,
    () => (stack ? stack.isBlocked(id) : false),
    () => false
  );

  return wantsToRender && !blocked;
}

/**
 * The same claim, plus a LIVE reader of the blocked state.
 *
 * Why this exists rather than just `useBannerSlot`. The boolean that hook
 * returns is captured at render time, and the claims it depends on are made in
 * a layout effect of that same commit. React flushes a commit's pending
 * passive effects before it processes the re-render that the layout-phase
 * claim scheduled, so a passive effect reading the captured value can see a
 * `true` for a banner that is already losing the slot. Rendering is unaffected
 * because the re-render lands before paint; anything that LATCHES on that
 * value is not.
 *
 * `isBlockedNow()` reads the store at call time, so a passive effect can ask
 * the question again and get the settled answer. The campaign banner uses it
 * to avoid recording a "prompted" event for a banner nobody saw.
 */
export function useBannerSlotWithLiveState(
  id: BannerId,
  wantsToRender: boolean
): { mayRender: boolean; isBlockedNow: () => boolean } {
  const stack = useContext(BannerStackContext);
  const mayRender = useBannerSlot(id, wantsToRender);

  const isBlockedNow = useCallback(
    () => (stack ? stack.isBlocked(id) : false),
    [stack, id]
  );

  return { mayRender, isBlockedNow };
}

function noopSubscribe(): () => void {
  return () => {};
}
